/**
 * Reprocess an invoice from the details page: start POST /v2/vip/invoices/reprocess,
 * poll the batch until done, then refresh the invoice. The parse can take minutes;
 * leaving the page stops the polling, not the reprocess.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { reprocessInvoice, getBatchStatus } from "../api/attachmentApi";
import { invoiceKeys } from "../api/queryKeys";
import { PROCESSING_STATUS, type BatchChildStatus } from "../types/attachment";

export const REPROCESS_POLL_MS = 5000;
/** Retries per poll before a non-404 error ends the run as status_error. */
export const REPROCESS_POLL_RETRIES = 3;

export type ReprocessOutcome =
  /** Clean re-read; the invoice went through validation again. */
  | { kind: "success" }
  /** Re-read was clean but validation failed; the invoice moved to Validation Failed. */
  | { kind: "validation_failed"; reason?: string }
  /** Re-read failed; the backend left the invoice exactly as it was. */
  | { kind: "unchanged"; reason?: string }
  /** Status no longer available (batch aged out, or no batch id); the refreshed page shows the result. */
  | { kind: "unknown" }
  /** Status polling kept failing (gateway error, expired session); the reprocess may still be running. */
  | { kind: "status_error" };

/** Backend errors are `{ error }`; 403s are rethrown by the api interceptor as `{ message }`. */
export function reprocessErrorMessage(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string };
  return e?.response?.data?.error || e?.message || "Failed to start reprocess";
}

/** OCR_COMPLETED = clean re-read whose validation gave no verdict; anything unexpected is "unknown". */
export function outcomeFor(child?: BatchChildStatus): ReprocessOutcome {
  switch (child?.status) {
    case PROCESSING_STATUS.VALIDATION_SUCCESS:
    case PROCESSING_STATUS.OCR_COMPLETED:
      return { kind: "success" };
    case PROCESSING_STATUS.VALIDATION_FAILED:
      return { kind: "validation_failed", reason: child.reason };
    case PROCESSING_STATUS.OCR_FAILED:
      return { kind: "unchanged", reason: child.reason };
    default:
      return { kind: "unknown" };
  }
}

const httpStatus = (err: unknown) => (err as { response?: { status?: number } } | null)?.response?.status;

export function useReprocessInvoice(
  invoiceId: number,
  opts: { onFinished: (outcome: ReprocessOutcome) => void },
) {
  const queryClient = useQueryClient();
  const [batchId, setBatchId] = useState<string | null>(null);

  const finish = (outcome: ReprocessOutcome) => {
    queryClient.invalidateQueries(invoiceKeys.detail(invoiceId));
    queryClient.invalidateQueries(invoiceKeys.attachments(invoiceId));
    queryClient.invalidateQueries(invoiceKeys.lists());
    queryClient.invalidateQueries(invoiceKeys.summary());
    opts.onFinished(outcome);
    setBatchId(null);
  };

  const start = useMutation({
    mutationFn: (attachmentId: string) => reprocessInvoice(invoiceId, attachmentId),
    onSuccess: (res) => {
      const id = res.batch_id ?? res.workflow_id ?? null;
      // Nothing to poll: refresh now rather than leave the run without an outcome.
      if (id) setBatchId(id);
      else finish({ kind: "unknown" });
    },
  });

  const status = useQuery({
    queryKey: invoiceKeys.batchStatus(batchId ?? ""),
    queryFn: () => getBatchStatus(batchId as string),
    enabled: !!batchId,
    refetchInterval: (data) => (data?.done ? false : REPROCESS_POLL_MS),
    // 404 = batch aged out of Temporal retention: stop and let the refresh show the state.
    retry: (count, err) => httpStatus(err) !== 404 && count < REPROCESS_POLL_RETRIES,
  });

  // A poll that still fails after its retries ends the run: 404 → unknown, anything else → status_error.
  const failed = !!batchId && status.isError;
  const finished = !!batchId && (status.data?.done || failed);

  useEffect(() => {
    if (!finished) return;
    if (failed) finish(httpStatus(status.error) === 404 ? { kind: "unknown" } : { kind: "status_error" });
    else finish(outcomeFor(status.data?.children?.[0]));
    // opts.onFinished is a fresh closure each render; firing once per batch is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  return {
    start: start.mutate,
    /** True from the click until the batch is done. */
    isRunning: start.isLoading || !!batchId,
  };
}
